import { daysUntil, money, relativeDays } from './format';
import type { KeyOut } from './types';

export const EXPIRY_WARN_DAYS = 14;
const LIMIT_WARN_PCT = 80;

export type Severity = 'blocked' | 'warning' | 'ok';

export interface KeyIssue {
  kind: 'disabled' | 'expired' | 'exhausted' | 'zero_limit' | 'expiring' | 'near_limit';
  severity: Severity;
  label: string;
  /** What the caller actually sees upstream, so a demo can map UI state to a real error. */
  detail: string;
}

export interface KeyState {
  severity: Severity;
  /** Short badge text for the row. */
  label: string;
  /** Every distinct reason, not just the first — these are separate causes with separate fixes. */
  issues: KeyIssue[];
}

/**
 * Mandatory 90-day expiry (PLAN.md) plus immutable expires_at upstream means an
 * expiring key needs a rotation, not an edit — so "expiring soon" is a real state,
 * not decoration.
 */
export function keyState(key: KeyOut, now: Date = new Date()): KeyState {
  const issues: KeyIssue[] = [];

  if (key.disabled) {
    issues.push({
      kind: 'disabled',
      severity: 'blocked',
      label: 'Disabled',
      detail: 'Disabled in OpenRouter. Requests fail with 401.',
    });
  }

  const expiryDays = daysUntil(key.expires_at, now);
  if (expiryDays !== null && expiryDays < 0) {
    issues.push({
      kind: 'expired',
      severity: 'blocked',
      label: 'Expired',
      detail: `Expired ${relativeDays(expiryDays)}. Requests fail with 401. Expiry is immutable upstream, so this needs a new key.`,
    });
  } else if (expiryDays !== null && expiryDays <= EXPIRY_WARN_DAYS) {
    issues.push({
      kind: 'expiring',
      severity: 'warning',
      label: 'Expiring soon',
      detail: `Expires ${relativeDays(expiryDays)}. Extending expiry requires rotation to a new key.`,
    });
  }

  // A null limit is unlimited; 0 is a hard block. Treat them as different worlds.
  if (key.limit_usd === 0) {
    issues.push({
      kind: 'zero_limit',
      severity: 'blocked',
      label: 'Zero limit',
      detail: 'Limit is set to $0, which blocks every request with 402.',
    });
  } else if (key.limit_remaining !== null && key.limit_remaining <= 0) {
    issues.push({
      kind: 'exhausted',
      severity: 'blocked',
      label: 'Limit reached',
      detail: 'No budget left in the current period. Requests fail with 402 until the limit resets or is raised.',
    });
  } else if (
    key.limit_usd !== null &&
    key.limit_usd > 0 &&
    key.limit_remaining !== null &&
    (1 - key.limit_remaining / key.limit_usd) * 100 >= LIMIT_WARN_PCT
  ) {
    issues.push({
      kind: 'near_limit',
      severity: 'warning',
      label: 'Near limit',
      detail: `${money(key.limit_remaining)} left of ${money(key.limit_usd)} in the current period.`,
    });
  }

  const blocked = issues.filter((i) => i.severity === 'blocked');
  if (blocked.length > 0) {
    return {
      severity: 'blocked',
      label: blocked.length === 1 ? blocked[0]!.label : 'Blocked',
      issues,
    };
  }
  if (issues.length > 0) {
    return { severity: 'warning', label: issues[0]!.label, issues };
  }
  return { severity: 'ok', label: 'Active', issues };
}
